import { useEffect } from "react";

/**
 * SITE_URL resolution order (first non-empty wins):
 *   1. REACT_APP_SITE_URL env var (set in production .env)
 *   2. window.location.origin (works on any deployed domain — preview or prod)
 *   3. Hardcoded prod fallback for SSR / build-time tag rendering
 *
 * This lets the preview environment keep the preview URL automatically while
 * production picks up https://thesaltymoo.com from the env var (or the browser).
 */
const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" && window.location?.origin) ||
  "https://thesaltymoo.com";

// Dedicated 1200×630 social share PNG (solid warm-cream background, no
// transparency). Always served from the production domain so previews render
// consistently across iMessage, X, Facebook, LinkedIn, Slack, etc.
const OG_IMAGE_URL = "https://thesaltymoo.com/og-image.png";
const DEFAULT_IMAGE = OG_IMAGE_URL;

/**
 * Lightweight SEO helper — sets document.title and writes meta tags imperatively
 * so we don't pull in a dependency like react-helmet. Each page calls <SEO ... />
 * once. On unmount we leave the tags in place (next page will overwrite them).
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function upsertLink(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
}) {
  useEffect(() => {
    const fullTitle = title.includes("Salty Moo")
      ? title
      : `${title} · The Salty Moo™`;
    document.title = fullTitle;

    const url = `${SITE_URL}${path}`;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: url });

    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: url,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: image,
    });
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: image,
    });
    upsertMeta('meta[property="og:image:type"]', {
      property: "og:image:type",
      content: "image/png",
    });
    upsertMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsertMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: "The Salty Moo\u2122 \u2014 Salt of the Herd",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "The Salty Moo™",
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: image,
    });
  }, [title, description, path, image, type]);

  return null;
}
